<template>
  <div v-if="featureEnabled" class="jfpwa" ref="wrapper">
    <!-- Picked state: collapsed breadcrumb with clear × -->
    <div v-if="hasPicked && !searchQuery" class="jfpwa__picked">
      <JobFamilyIcon size="normal" class="jfpwa__picked-icon" />
      <div class="jfpwa__breadcrumb">
        <span class="jfpwa__breadcrumb-ancestor">{{ pickedFamily?.jobFamilyName }}</span>
        <span class="jfpwa__breadcrumb-sep">›</span>
        <span class="jfpwa__breadcrumb-leaf">{{ formatLevelLabel(pickedLevel) }}</span>
      </div>
      <span v-if="pickedIdLabel" class="jfpwa__code" data-test="jfpwa-picked-id">#{{ pickedIdLabel }}</span>
      <div
        class="jfpwa__clear"
        role="button"
        tabindex="0"
        aria-label="Clear selection"
        @click.stop="handleClear"
        @keydown.enter.stop="handleClear"
        @keydown.space.stop.prevent="handleClear"
      >
        <Icon name="x_close_icon" />
      </div>
    </div>

    <!-- Search input (shown in all non-picked states) -->
    <div v-else class="jfpwa__search-wrapper" ref="searchWrapper">
      <ExTextInput
        :value="searchQuery"
        :ghostText="placeholder"
        :aria-label="placeholder"
        icon="interface_search"
        :hideFloatingLabel="true"
        @input="onInput"
        ref="searchInputRef"
      />

      <!-- Results dropdown (only when query exists). Portaled to document.body while
           open so it escapes the parent modal's overflow/stacking contexts. -->
      <div
        v-if="showDropdown"
        ref="dropdown"
        class="jfpwa__dropdown"
        role="listbox"
        :style="dropdownStyle"
        :data-jfpwa-dropdown="portalId"
      >
        <!-- Loading state: skeleton rows -->
        <div v-if="loading" class="jfpwa__skeleton-list">
          <div
            v-for="i in 5"
            :key="i"
            class="jfpwa__skeleton-row"
          >
            <div class="jfpwa__skeleton-bar" :style="{ width: skeletonWidths[i - 1] }"></div>
          </div>
        </div>

        <!-- Results / Add / Add with matches states -->
        <template v-else>
          <!-- Add Job Family CTA (shown when no exact match) -->
          <button
            v-if="showAddCta"
            type="button"
            class="jfpwa__add-cta"
            @click="handleAddJobFamily"
          >
            <span class="jfpwa__add-left">
              <img
                :src="addIconSvg"
                alt=""
                aria-hidden="true"
                class="jfpwa__add-icon"
              />
              <span class="jfpwa__add-label">Add Job Family "{{ searchQuery.trim() }}"</span>
            </span>
            <span class="jfpwa__add-counter" data-testid="jfpwa-add-counter">{{ remainingCharacters }}</span>
          </button>

          <!-- Result rows (families with levels) -->
          <div v-if="flattenedResults.length > 0" class="jfpwa__results-list">
            <div
              v-for="row in flattenedResults"
              :key="`${row.jobFamilyId}-${row.levelId}`"
              role="option"
              tabindex="0"
              class="jfpwa__row"
              @click="selectRow(row)"
              @keydown.enter="selectRow(row)"
              @keydown.space.prevent="selectRow(row)"
            >
              <JobFamilyIcon size="normal" class="jfpwa__row-icon" />
              <div class="jfpwa__row-path">
                <span class="jfpwa__breadcrumb-ancestor">{{ row.jobFamilyName }}</span>
                <span class="jfpwa__breadcrumb-sep">›</span>
                <span class="jfpwa__breadcrumb-leaf">{{ formatLevelLabel(row.level) }}</span>
              </div>
              <span v-if="row.code" class="jfpwa__row-code">#{{ row.code }}</span>
            </div>
          </div>

          <!-- No results (when not loading, has query, no matches, and not showing CTA alone) -->
          <div v-else-if="!showAddCta" class="jfpwa__no-results">
            <span class="jfpwa__no-results-text">No job families found</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted, onUnmounted, nextTick } from "vue";
import Icon from "@components/icons/Icon.vue";
import JobFamilyIcon from "@components/icons/items/OrgObject/JobFamilyIcon.vue";
import addIconSvg from "@components/assets/add_icon.svg";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import {
  JOB_FAMILY_NAME_MIN_CHARACTERS,
  JOB_FAMILY_NAME_MAX_CHARACTERS,
} from "@components/constants/jobFamily.js";
import {
  sortLevelsDescending,
  formatLevelLabel as formatLevelLabelUtil,
  findFamilyById,
  findLevelById,
} from "@components/JobFamilyPickerDialog/jobFamilyPickerDialogUtils.js";

/**
 * JobFamilyPickerWithAdd — ER-11726. Inline job-family + level picker field for the
 * position form: a placeholder search box that expands to type-to-search results rendered
 * as flat Family › ordinal · Level rows, collapses to a picked breadcrumb, and shows a
 * "+ Add Job Family" CTA when the typed query has no exact Family-name match.
 *
 * Sibling of JobFamilyPickerDialog (ER-11645) — same row/CTA styling and behavior rules,
 * but no modal chrome. Modeled on DepartmentPickerField but fixed to a two-segment path
 * since Job Family is never nested. Behind the idea-MDP-581 kill switch shared with the
 * rest of Job Families.
 */
const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  results: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  query: {
    type: String,
    default: "",
  },
  /** Show the picked Level id (instead of the Family code) as the right-aligned "#<id>" tag. */
  showLevelId: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "search", "add-job-family", "clear"]);

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const featureEnabled = ref(true);
const portalId = `jfpwa-dropdown-${instance?._uid ?? instance?.uid ?? Date.now()}`;

const searchQuery = ref(props.query || "");
const wrapper = ref(null);
const searchInputRef = ref(null);
const searchWrapper = ref(null);
const dropdown = ref(null);
const dropdownStyle = ref({});
let searchDebounceTimeout = null;

const placeholder = "Search job families by name or code";
const skeletonWidths = ["62%", "78%", "55%", "44%", "62%"];

// The field is controlled from above: a parent-supplied `query` (Storybook stories and the
// position form both set it) seeds and re-syncs the visible search text. Comparing before
// assigning keeps local typing and a parent echoing the debounced `search` value from
// fighting each other.
watch(
  () => props.query,
  (value) => {
    const nextQuery = value || "";
    if (nextQuery !== searchQuery.value) searchQuery.value = nextQuery;
  }
);

// The search `results` may not hold the picked family yet (a saved value seeded before any
// search runs), so fall back to the names carried on the value itself.
const pickedFamily = computed(() => {
  const value = props.modelValue;
  if (!value?.jobFamilyId) return null;
  const found = findFamilyById(props.results, value.jobFamilyId);
  if (found) return found;
  if (!value.jobFamilyName) return null;
  return { jobFamilyId: value.jobFamilyId, jobFamilyName: value.jobFamilyName, code: value.code, levels: value.levels };
});

const pickedLevel = computed(() => {
  if (!pickedFamily.value || !props.modelValue?.levelId) return null;
  return findLevelById(pickedFamily.value, props.modelValue.levelId) || props.modelValue.level || null;
});

const hasPicked = computed(() => Boolean(pickedFamily.value));

const pickedIdLabel = computed(() =>
  props.showLevelId ? props.modelValue?.levelId ?? "" : pickedFamily.value?.code ?? ""
);

const showDropdown = computed(() => Boolean(searchQuery.value.trim()));

const flattenedResults = computed(() => {
  if (!Array.isArray(props.results)) return [];
  const flattened = [];
  for (const family of props.results) {
    const sortedLevels = sortLevelsDescending(family.levels);
    for (const level of sortedLevels) {
      flattened.push({
        jobFamilyId: family.jobFamilyId,
        jobFamilyName: family.jobFamilyName,
        code: family.code,
        levelId: level.id,
        level,
      });
    }
  }
  return flattened;
});

const showAddCta = computed(() => {
  const typedLength = searchQuery.value.trim().length;
  const withinLength =
    typedLength >= JOB_FAMILY_NAME_MIN_CHARACTERS &&
    typedLength <= JOB_FAMILY_NAME_MAX_CHARACTERS;
  return withinLength && !props.loading;
});

const remainingCharacters = computed(() => {
  return JOB_FAMILY_NAME_MAX_CHARACTERS - searchQuery.value.trim().length;
});

function formatLevelLabel(level) {
  return formatLevelLabelUtil(level);
}

function onInput(value) {
  searchQuery.value = value;
  clearTimeout(searchDebounceTimeout);
  searchDebounceTimeout = setTimeout(() => {
    emit("search", searchQuery.value);
  }, 300);
}

function selectRow(row) {
  searchQuery.value = "";
  emit("update:modelValue", {
    jobFamilyId: row.jobFamilyId,
    levelId: row.levelId,
  });
}

function handleClear() {
  emit("update:modelValue", null);
  emit("clear");
}

function handleAddJobFamily() {
  emit("add-job-family", searchQuery.value.trim());
}

function handleOutsideClick(e) {
  const insideWrapper = wrapper.value && wrapper.value.contains(e.target);
  const insideDropdown = dropdown.value && dropdown.value.contains(e.target);
  if (insideWrapper || insideDropdown) return;
  if (searchQuery.value && !props.modelValue) {
    searchQuery.value = "";
    emit("search", "");
  }
}

/* ---- Portaled dropdown (Vue 2 has no Teleport) ---------------------------
 * The dropdown is absolutely-positioned by default but, while open, is appended
 * to document.body and switched to position:fixed so it escapes the parent
 * modal's overflow/stacking contexts. The form below keeps its layout (out of
 * flow), while the popover floats above sibling fields at the modal-overlay
 * z-index layer and re-anchors to the search input on scroll/resize.
 * ------------------------------------------------------------------------ */
function attachDropdownToBody() {
  const el = dropdown.value;
  if (el && el.parentNode !== document.body) {
    document.body.appendChild(el);
  }
}

function removePortaledDropdownFromDom() {
  const el = dropdown.value;
  if (el?.parentNode) {
    el.parentNode.removeChild(el);
    return;
  }
  document.querySelector(`[data-jfpwa-dropdown="${portalId}"]`)?.remove();
}

function updateDropdownPosition() {
  const anchor = searchWrapper.value;
  if (!anchor) return;
  const rect = anchor.getBoundingClientRect();
  const menuHeight = dropdown.value?.offsetHeight ?? 0;
  const gap = 8;
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = menuHeight > 0 && spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;
  const top = openAbove ? rect.top - menuHeight - gap : rect.bottom + gap;

  dropdownStyle.value = {
    position: "fixed",
    top: `${Math.round(top)}px`,
    left: `${Math.round(rect.left)}px`,
    width: `${Math.round(rect.width)}px`,
    zIndex: 10001,
  };
}

function syncOpenDropdown() {
  nextTick(() => {
    attachDropdownToBody();
    updateDropdownPosition();
    nextTick(updateDropdownPosition);
  });
}

function openDropdown() {
  syncOpenDropdown();
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
}

function closeDropdown() {
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
  nextTick(removePortaledDropdownFromDom);
}

watch(showDropdown, (open) => {
  if (open) {
    openDropdown();
  } else {
    closeDropdown();
  }
});

onMounted(async () => {
  document.addEventListener("click", handleOutsideClick);
  // Controlled consumers (e.g. Storybook) can mount with an initial query — the
  // watcher won't fire for that pre-opened state, so sync the portal once here.
  if (showDropdown.value) openDropdown();
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the component.
    featureEnabled.value = true;
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleOutsideClick);
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
  removePortaledDropdownFromDom();
  clearTimeout(searchDebounceTimeout);
});
</script>

<style scoped>
.jfpwa {
  width: 100%;
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.jfpwa__search-wrapper {
  position: relative;
  width: 100%;
}

.jfpwa__dropdown {
  font-family: Inter, sans-serif;
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 10;
  /* Portaled to document.body — must set Inter here; it no longer inherits from .jfpwa */
  font-family: Inter, sans-serif;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 6px -2px rgba(16, 24, 40, 0.03), 0 12px 16px -4px rgba(16, 24, 40, 0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  max-height: 280px;
  overflow-y: auto;
}

.jfpwa__picked {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px 10px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
  min-height: 44px;
}

.jfpwa__picked-icon {
  flex-shrink: 0;
}

.jfpwa__breadcrumb {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  font-size: 14px;
  line-height: 20px;
  gap: 6px;
}

.jfpwa__breadcrumb-ancestor {
  color: #475467;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}

.jfpwa__breadcrumb-sep {
  color: #d0d5dd;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}

.jfpwa__breadcrumb-leaf {
  color: #6941c6;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
}

.jfpwa__code {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 400;
  color: #475467;
  line-height: 20px;
  white-space: nowrap;
}

.jfpwa__row-code {
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #475467;
  line-height: 20px;
  white-space: nowrap;
  margin-left: auto;
}

.jfpwa__clear {
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #98a2b3;
  padding: 2px;
  border-radius: 4px;
}

.jfpwa__clear:hover {
  color: #667085;
  background: #f2f4f7;
}

.jfpwa__skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 8px;
}

.jfpwa__skeleton-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  height: 40px;
  box-sizing: border-box;
}

.jfpwa__skeleton-bar {
  height: 16px;
  background: #eaecf0;
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.jfpwa__add-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: calc(100% - 16px);
  margin: 0 8px;
  padding: 10px 12px;
  background: #7f56d9;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  height: 46px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}

.jfpwa__add-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.jfpwa__add-cta:hover {
  background: #6941c6;
  border-color: #6941c6;
}

.jfpwa__add-cta:focus {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.jfpwa__add-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  filter: brightness(0) invert(1);
}

.jfpwa__add-label {
  color: #ffffff;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.jfpwa__add-counter {
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  line-height: 16.94px;
  flex-shrink: 0;
}

.jfpwa__results-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 8px;
}

.jfpwa__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #f2f4f7;
  border-radius: 10px;
  height: 40px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}

.jfpwa__row:hover {
  background: #f9fafb;
  border-color: #e5e7eb;
}

.jfpwa__row:focus {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.jfpwa__row-icon {
  flex-shrink: 0;
}

.jfpwa__row-path {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
}

.jfpwa__no-results {
  padding: 20px;
  text-align: center;
}

.jfpwa__no-results-text {
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
</style>
